import "./input-select.scss";

import { ChangeEvent, KeyboardEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { InputSelectOption, InputSelectProps } from "src/types";
import { concatClasses } from "src/utils";

export const useInputSelect = ({
  name = "",
  value: selectedOptionId,
  touched,
  disabled,
  error,
  setFieldValue,
  setFieldTouched,
  validateField,
  handleChange,
  classesInput = "input-select--input",
  classesHead = "input-select--head",
  classesField = "input-select--field",
  classesError = "input-select--error",
  classesIcon = "input-select--icon filter-primary",
  options,
}: InputSelectProps) => {
  const { t } = useTranslation();

  const [expanded, setExpanded] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const filteredOptions = options?.filter((option) =>
    option.value.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setExpanded(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  ////////////////////////////////////////////////////////////////////////////////////////////////////

  const toggleMenu = () => setExpanded((x) => !x);

  const updateTouched = () => {
    if (expanded) {
      setFieldTouched?.(name, true);
    } else {
      setSearchTerm("");
    }
  };

  const updateOption = (option?: InputSelectOption) => {
    setExpanded(false);
    if (option) setFieldValue?.(name, option.id);
    else setFieldValue?.(name, "");
    if (handleChange) {
      handleChange({
        target: { name, value: option },
      } as ChangeEvent<any>);
    }
    setTimeout(() => validateField?.(name));
  };

  ////////////////////////////////////////////////////////////////////////////////////////////////////

  const conditionalClasses = (() => {
    const base = {
      head: "",
      field: "input-select--border-normal",
      error: "opacity-0",
    };

    if (touched && error) {
      base.field = "input-select--border-error";
      base.error = "";
    }

    if (expanded) {
      base.head += " input-select--border-no-radius";
      base.field += " input-select--border-no-radius";
    }

    return base;
  })();
  ////////////////////////////////////////////////////////////////////////////////////////////////////

  const allClassesArrow = concatClasses(
    classesIcon,
    expanded && !disabled ? "" : "input-select--rotate"
  );

  const allClassesInput = concatClasses(
    classesInput,
    selectedOptionId ? "" : "color-disabled"
  );

  const allClassesHead = concatClasses(
    classesHead,
    conditionalClasses.head,
    disabled ? "input-select--head-disabled" : "cursor-pointer"
  );

  const allClassesField = concatClasses(classesField, conditionalClasses.field);

  const allClassesError = concatClasses(classesError, conditionalClasses.error);

  ////////////////////////////////////////////////////////////////////////////////////////////////////

  const onKeyDownHead = (e: KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;

    e.stopPropagation();

    if (
      e.key === "Enter" ||
      e.key === " " ||
      (e.key === "ArrowDown" && !expanded) ||
      (e.key === "ArrowUp" && expanded)
    ) {
      e.preventDefault();
      toggleMenu();
    }
  };

  const onKeyDownOption = (
    e: KeyboardEvent<HTMLDivElement>,
    option?: InputSelectOption
  ) => {
    if (disabled) return;

    if (e.key === "Enter") {
      updateOption(option);
    }
  };

  const onSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!expanded) toggleMenu();
    setSearchTerm(e.target.value);
  };

  return {
    expanded,
    allClassesArrow,
    classesIcon,
    allClassesInput,
    allClassesHead,
    allClassesField,
    allClassesError,
    searchTerm,
    filteredOptions,
    dropdownRef,
    searchInputRef,
    t,
    toggleMenu,
    updateTouched,
    updateOption,
    onKeyDownHead,
    onKeyDownOption,
    onSearchChange,
  };
};
